import {
  GoABlock,
  GoAButton,
  GoACheckbox,
  GoADropdown,
  GoADropdownItem,
  GoAFormItem,
  GoAPagination,
  GoASpacer,
  GoATable,
  GoATableSortHeader,
} from "@abgov/react-components";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../../components/PageHeader";
import PageLoader from "../../components/PageLoader";
import moment from "moment";
import SearchInput, { SearchInputRef } from "../../components/searchInput";
import { Fragment, useEffect, useRef, useState } from "react";
import { orderFromOptions, orderTypeOptions, permissions } from "../../common/constants";
import { Order, SearchParams } from "../../model/Order";
import { OrderService } from "../../services/OrderService";
import { Breadcrumbs, Link, debounce, Typography, Drawer, IconButton } from "@mui/material";
import { CustomFmF } from "../../components/datePicker";
import { getDataFromSessionStorage, saveDataToSessionStorage } from "../../utils/SessionStorageUtils";
import { ForObjectType } from "../../model/enums/Constants";
import { loadCommonLookups } from "../../states/Common.state";
import { CommonLookup } from "../../model/Common";
import { ExternalService, ISearch } from "../../services/ExternalService";
import MultiSelectDropdown from "../../components/MultiSelectDropdown";
import { forestAreaState } from "../../states/Order.state";
import { SelectOptions } from "../../model/Base";
import { useRecoilState } from "recoil";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from "@mui/icons-material/Close";
import { useAuth } from "react-oidc-context";
import { getUserName, hasResourceRole } from "../../utils/tokens";
import OrderStatusBadge from "../../components/OrderStatusBadge";
import dayjs from "dayjs";

const OrdersList = () => {

  const auth = useAuth();

  const [userName, setUserName] = useState(getUserName(auth?.user?.access_token ?? ""));

  const hasProcessorPermission = hasResourceRole(
    String(process.env.REACT_APP_RESOURCEORDERS_CLIENT),
    permissions.ORDER_STATUS_UPDATE,
    auth.user?.access_token ?? ""
  );



  // Navigation
  const navigate = useNavigate();
  //Loader
  const [loading, setIsLoading] = useState(true);
  //Search
  const [searchValue, setSearchValue] = useState(getDataFromSessionStorage("searchValue") || "");
  const [orderData, setOrderData] = useState<Order[]>([]);

  // page number
  const [page, setPage] = useState(getDataFromSessionStorage("page") || 1);

  //count per page
  const [perPage, setPerPage] = useState(getDataFromSessionStorage("perPage") || 50);

  const [totalCount, setTotalCount] = useState(0);

  //Sorting
  const [sortCol, setSortCol] = useState(getDataFromSessionStorage("sortCol") || "lastUpdatedDate");
  const [sortDir, setSortDir] = useState(getDataFromSessionStorage("sortDir") || 0);
  const [orderStatusOptions, setOrderStatusOptions] = useState<{ label: string; value: number }[]>([]);
  const [forestAreaOptions, setForestAreaOptions] = useRecoilState<SelectOptions[]>(forestAreaState);
  const [fireNumberOptions, setFireNumberOptions] = useState<{ label: string; value: string }[]>([]);
  const [crewNumberOptions, setCrewNumberOptions] = useState<{ label: string; value: string }[]>([]);
  const fireNumberDropdownRef = useRef<any>(null);
  const crewNumberDropdownRef = useRef<any>(null);
  const orderFromDropdownRef = useRef<any>(null);
  const forestAreaDropdownRef = useRef<any>(null);
  const orderStatusDropdownRef = useRef<any>(null);
  const orderTypeDropdownRef = useRef<any>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [applyFilter, setApplyFilter] = useState(true);

  const filterSessionData = getDataFromSessionStorage("searchParamsValues");
  const [showMyOrder, setShowMyOrder] = useState(filterSessionData ? filterSessionData.orderedBy !== "" : !hasProcessorPermission);

  const [searchParams, setSearchParams] = useState<SearchParams>(
    getDataFromSessionStorage("searchParams") ||
    {
      fireNumber: [],
      crewNumber: [],
      orderFrom: [],
      forestArea: [],
      orderStatus: [],
      orderType: [],
      startDate: null,
      endDate: null,
      orderedBy: showMyOrder ? userName : "",
    }
  );

  const [searchParamsValues, setSearchParamsValues] = useState<SearchParams>(
    (() => {
      return filterSessionData
        ? {
          ...filterSessionData,
          startDate: filterSessionData.startDate ? new Date(filterSessionData.startDate) : null,
          endDate: filterSessionData.endDate ? new Date(filterSessionData.endDate) : null,
          orderedBy: showMyOrder ? userName : "",
        }
        : {
          fireNumber: null,
          crewNumber: null,
          orderFrom: null,
          forestArea: null,
          orderStatus: null,
          orderType: null,
          startDate: null,
          endDate: null,
          orderedBy: showMyOrder ? userName : ""
        };
    })()
  );

  const toggleDrawer = (open: boolean) => () => {
    setDrawerOpen(open);
  };

  const searchInputRef = useRef<SearchInputRef>(null);

  const updateSearchValueFromParent = () => {
    if (searchInputRef.current) {
      searchInputRef.current.setSearchValue('');
    }
  };

  React.useEffect(() => {
    document.body.style.overflow = 'auto';
    // Scroll to top when the component mounts
    window.scrollTo(0, 0);
  }, []);

  React.useEffect(() => {
    if (searchInputRef.current)
      searchInputRef.current.setSearchValue(searchValue);
  }, [searchValue]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  React.useEffect(() => {
    if (!userName) {
      setUserName(getUserName(auth?.user?.access_token ?? ""));
    }
  }, [auth?.user?.access_token, userName]);

  useEffect(() => {

    const fetchData = async () => {
      try {
        const loadItems = debounce(async (callback: (data: any) => void) => {
          setIsLoading(true);
          const response = await OrderService.getAll(
            searchValue,
            searchParamsValues,
            page,
            perPage,
            sortCol,
            sortDir === 1 ? "ASC" : "DESC"
          );

          setIsLoading(false);
          // Pass the transformed options to the callback function
          callback(response.data);
        }, 200);

        loadItems((result: any) => {
          if (result?.pageInfo?.currentPage > 0) {
            setPage(result?.pageInfo?.currentPage);
          }
          setTotalCount(result?.pageInfo?.total ?? 0);
          setOrderData(result.items);
        });


      } catch (error) {
        console.error('Error fetching location data:', error);

      }
      finally {
        setIsLoading(false);
      }
    };

    if (applyFilter || isMobile || sortCol || sortDir) {
      fetchData();
      setApplyFilter(false);
    }

    saveDataToSessionStorage("searchParamsValues", searchParamsValues);
    saveDataToSessionStorage("searchParams", searchParams);
    saveDataToSessionStorage("searchValue", searchValue);
    saveDataToSessionStorage("sortCol", sortCol);
    saveDataToSessionStorage("sortDir", sortDir);
    saveDataToSessionStorage("page", page);
    saveDataToSessionStorage("perPage", perPage);


  }, [isMobile, applyFilter, searchParamsValues, searchValue, searchParams, page, perPage, sortCol, sortDir, setOrderData, setTotalCount]);

  React.useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);

      let orderLookups = getDataFromSessionStorage(ForObjectType.ORDER);

      if (!orderLookups || !orderLookups.elements || orderLookups.elements.length === 0) {
        await loadCommonLookups();
        orderLookups = getDataFromSessionStorage(ForObjectType.ORDER);
      }

      const orderStatusLookups = orderLookups
        ?.find((x: CommonLookup) => x.name === "status")
        ?.elements ?? [];

      const transformToOptions = (inputArray: any[], useLookupFields = false) =>
        inputArray.map((item) => ({
          label: useLookupFields ? item.value : item.name,
          value: useLookupFields ? item.id : item.name,
        }));

      setOrderStatusOptions(transformToOptions(orderStatusLookups, true));

      const objISearch: ISearch = {
        search: "",
        filterBy: { columnName: "CRT_ID", columnValue: "4" },
        pagination: { perPage: 20, page: 1 },
      };

      try {
        const response = await ExternalService.getCorporateRegion(objISearch);
        setForestAreaOptions(transformToOptions(response.data));
      } catch (e) {
        console.error("Error fetching regions:", e);
      }

      setIsLoading(false);
    };

    fetchData();

  }, [setOrderStatusOptions, setForestAreaOptions]);

  const onFireNoSearchChange = (inputValue: string) => {

    const lowerCasedInput = inputValue.toLowerCase();
    loadFireOptions(lowerCasedInput, (result) => {
      if (result) {
        const options = result?.map((r) => ({
          label: `${r.fireNumber}-${r.year}`, // Display text for the dropdown
          value: `${r.fireNumber}-${r.year}`  // Underlying value
        }));
        setFireNumberOptions(options);
      }
    });
  };

  const onCrewNumberSearchChange = (inputValue: string) => {

    const lowerCasedInput = inputValue.toLowerCase();
    loadCrewNumberOptions(lowerCasedInput, (result) => {
      let options = [];
      if (result?.length > 0) {
         options = result?.map((r) => ({
          label: r.crewNumber, // Display text for the dropdown
          value: r.crewNumber // Underlying value
        }));
      }
      else{
         options.push({label: lowerCasedInput,
          value: lowerCasedInput });
        }
        setCrewNumberOptions(options);
      });
  };

  const loadFireOptions = debounce(async (inputValue: string, callback: (data: any[]) => void) => {
    if (!inputValue) return;
    const currentYearFireNumbers = await ExternalService.getFireNumbers(inputValue) || [];
    // Pass the transformed options to the callback function
    callback(currentYearFireNumbers);
  }, 500);

  const loadCrewNumberOptions = debounce(async (inputValue: string, callback: (data: any[]) => void) => {
    if (!inputValue) return;
    const crewNumbers = await ExternalService.getCrewNumbers(inputValue, undefined) || [];
    // Pass the transformed options to the callback function
    callback(crewNumbers);
  }, 500);



  function searchOrders(searchParamValue: string) {
    if (searchParamValue.length >= 3 && searchValue !== searchParamValue) {
      setPage(1);
      setSearchValue(searchParamValue);
    } else if (searchParamValue.length === 0 && searchValue !== "") {
      setSearchValue("");
    }
  }


  function sortData(sortBy: string, sortDir: number) {
    setSortCol(sortBy);
    setSortDir(sortDir);
  }

  function changePerPage(name: any, newPage: any) {
    if (newPage !== page) {
      setPerPage(newPage);
    }
  }

  //Pagination change page
  function changePage(newPage: any) {
    if (newPage) {
      setPerPage(perPage);
      setPage(newPage);
    }
  }

  const clearSearch = () => {
    setSearchValue('');
  };


  const clearAll = () => {
    fireNumberDropdownRef.current.clear();
    crewNumberDropdownRef.current.clear();
    orderFromDropdownRef.current.clear();
    forestAreaDropdownRef.current.clear();
    orderStatusDropdownRef.current.clear();
    orderTypeDropdownRef.current.clear();
    clearSearch();
    updateSearchValueFromParent();
    setSearchParamsValues({
      fireNumber: null,
      crewNumber: null,
      orderFrom: null,
      forestArea: null,
      orderStatus: null,
      orderType: null,
      startDate: null,
      endDate: null,
      orderedBy: ''
    });
    setSortCol('lastUpdatedDate')
    setSortDir('');
    setPage(1);
    setPerPage(50);
    setShowMyOrder(false);
  }

  const handleChange = (field: string, newValue: any) => {

    setSearchParams((prevValues) => ({
      ...prevValues,
      [field]: newValue,
    }));


    if (field === "startDate" || field === "endDate") {

      setSearchParams((prevValues) => ({
        ...prevValues,
        [field]: newValue
      }));


      setSearchParamsValues((prevValues) => {
        let value = newValue?.target.value;

        if (field === "startDate" && value) {
          const dateObject = new Date(value);

          // Check if the date object is valid
          if (!isNaN(dateObject.getTime())) {
            dateObject.setHours(0, 0, 0, 0);
            value = dateObject;
          }
        }
        if (field === "endDate" && value) {
          const dateObject = new Date(value);

          // Check if the date object is valid
          if (!isNaN(dateObject.getTime())) {
            dateObject.setHours(23, 59, 59, 999);
            value = dateObject;
          }
        }

        return {
          ...prevValues,
          [field]: value,
        };
      });
    }

    else if (newValue?.length > 0) {
      const newValues = newValue.map((option: any) => option.value).join(',');
      setSearchParamsValues((prevValues) => ({
        ...prevValues,
        [field]: newValues ? newValues : null,
      }));
    } else {
      setSearchParamsValues((prevValues) => ({
        ...prevValues,
        [field]: newValue,
      }));
    }

    if (field === "showMyOrder") {
      setSearchParamsValues((prevValues) => ({
        ...prevValues,
        orderedBy: newValue ? userName : "",
      }));

      setShowMyOrder(newValue);
      setApplyFilter(true);
    }
  }

  // const hasViewOrderPermission =
  //  hasResourceRole(
  //   String(process.env.REACT_APP_RESOURCEORDERS_CLIENT),
  //   permissions.ORDER_VIEW,
  //   auth.user?.access_token ?? ""
  // );

  const hasCreateOrderPermission = hasResourceRole(
    String(process.env.REACT_APP_RESOURCEORDERS_CLIENT),
    permissions.ORDER_CREATE,
    auth.user?.access_token ?? ""
  );

  return (
    <>
      {isMobile ? (
        <>
          <PageLoader visible={loading} />
          <div className="header-container">
            <Breadcrumbs className="navigation-buttons" aria-label="breadcrumb" separator={<ArrowForwardIcon fontSize="small" />}>
              <Link
                variant="body2"
                onClick={() => (window.location.pathname = '/')}
                style={{ cursor: 'pointer' }}
              >
                Home
              </Link>
              <Typography color="textPrimary">Orders</Typography>
            </Breadcrumbs>
            <div className="header-row">

              <div className="left-content">
                <PageHeader title="Orders" />
              </div>
              {hasCreateOrderPermission && (
                <div className="create-order-wrapper">
                  <GoAButton
                    type="primary"
                    trailingIcon="add"
                    onClick={() => {
                      navigate(`/orderNew`);
                    }}
                  >
                    Create order
                  </GoAButton>
                </div>
              )}
            </div>

            <div className="filter-row">
              <GoAButton
                type="secondary"
                onClick={toggleDrawer(true)}
              >
                Filter
              </GoAButton>
              <div className="show-my-orders">
                <GoACheckbox
                  name="showMyOrder"
                  text="Only show orders I submitted"
                  onChange={
                    handleChange
                  }
                  checked={showMyOrder}
                />
              </div>
            </div>
          </div>

          <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)}>
            <div className="drawer-content">
              <div className="drawer-header">
                <IconButton aria-label="close drawer" onClick={toggleDrawer(false)}>
                  <CloseIcon />
                </IconButton>
                <Typography variant="h6">Filters</Typography>
              </div>
              <div className="left width100">
                <Link
                  variant="body2"
                  onClick={clearAll}
                  style={{
                    cursor: 'pointer',
                    font: 'var(--goa-typography-heading-s)',
                    bottom: '0',
                  }}
                >
                  Clear all
                </Link>
              </div>

              <div className="left width100">
                <div className="left width100 order-search-container">
                  <div>
                    <SearchInput maxLength={250} label="" ref={searchInputRef} search={searchOrders} clearSearch={clearSearch} placeholder="Search by order number, ordered by, ship to"></SearchInput>
                  </div>
                </div>
              </div>
              <div className="left width100">
                <div className="left width100 order-search-container">
                  <div>
                    <GoAFormItem label="Fire number">
                      <MultiSelectDropdown
                        ref={fireNumberDropdownRef}
                        label=""
                        fieldName="fireNumber"
                        options={fireNumberOptions}
                        value={searchParams.fireNumber}
                        onChange={handleChange}
                        onInputChange={(event, value) => {
                          onFireNoSearchChange(value.includes("-") ? value.split("-")[0] : value);
                        }}
                      />
                    </GoAFormItem>
                  </div>
                  <div>
                    <GoAFormItem label='Submitted to'>
                      <MultiSelectDropdown
                        ref={orderFromDropdownRef}
                        label=""
                        fieldName="orderFrom"
                        options={orderFromOptions}
                        value={searchParams.orderFrom}
                        onChange={handleChange}
                      />
                    </GoAFormItem>
                  </div>
                  <div>
                    <GoAFormItem label='Forest area'>
                      <MultiSelectDropdown
                        ref={forestAreaDropdownRef}
                        label=""
                        fieldName="forestArea"
                        options={forestAreaOptions || []}
                        value={searchParams.forestArea}
                        onChange={handleChange}
                      />
                    </GoAFormItem>
                  </div>
                </div>
              </div>

              <div className="left width100">
                <div className="left width100 order-search-container">
                  <div>
                    <GoAFormItem label='Order status'>
                      <MultiSelectDropdown
                        ref={orderStatusDropdownRef}
                        label=""
                        fieldName="orderStatus"
                        options={orderStatusOptions}
                        value={searchParams.orderStatus}
                        onChange={handleChange}
                      />
                    </GoAFormItem>
                  </div>
                  <div>
                    <GoAFormItem label='Order type'>
                      <MultiSelectDropdown
                        ref={orderTypeDropdownRef}
                        label=""
                        fieldName="orderType"
                        options={orderTypeOptions}
                        value={searchParams.orderType}
                        onChange={handleChange}
                      />
                    </GoAFormItem>
                  </div>
                  <GoAFormItem label="Date range(start)">
                    <CustomFmF icon={searchParamsValues.startDate ? 'close' : 'calendar'} format="yyyy-MM-dd" value={searchParamsValues.startDate} onChange={(value: Date | null) => handleChange('startDate', value)} onBlur={(val: any) => { }} />
                  </GoAFormItem>
                  <GoAFormItem label="Date range(end)">
                    <CustomFmF icon={searchParamsValues.endDate ? 'close' : 'calendar'} format="yyyy-MM-dd" value={searchParamsValues.endDate} onChange={(value: Date | null) => handleChange('endDate', value)} onBlur={(val: any) => { }} />
                  </GoAFormItem>
                  <GoAFormItem label="Crew number">
                    <MultiSelectDropdown
                        ref={crewNumberDropdownRef}
                        label=""
                        fieldName="crewNumber"
                        options={crewNumberOptions}
                        value={searchParams.crewNumber}
                        onChange={handleChange}
                        onInputChange={(event, value) => {
                          onCrewNumberSearchChange(value);
                        }}
                      />
                  </GoAFormItem>

                  <GoAButton
                    type="primary"
                    trailingIcon="filter"
                    onClick={() => {
                      setApplyFilter(true);
                      setDrawerOpen(false);
                    }}
                  >
                    Apply filter
                  </GoAButton>
                </div>
              </div>
            </div>
          </Drawer>

          <div className="divTable">
            <GoATable onSort={sortData} width="100%">
              <thead>
                <tr>
                  <th>
                    <GoATableSortHeader name="orderNumber">Order #</GoATableSortHeader>
                  </th>
                  <th>
                    <GoATableSortHeader name="fireNumber">Fire #</GoATableSortHeader>
                  </th>
                  <th>
                    <GoATableSortHeader name="forestArea">Forest area</GoATableSortHeader>
                  </th>
                  <th>
                    <GoATableSortHeader name="orderBy">Ordered by</GoATableSortHeader>
                  </th>
                  <th>
                    <GoATableSortHeader name="status">Status</GoATableSortHeader>
                  </th>
                  <th>
                    <GoATableSortHeader name="submittedDate">Submitted date</GoATableSortHeader>
                  </th>
                  {/* <th>
                    <GoATableSortHeader name="orderDate">Order date</GoATableSortHeader>
                  </th> */}
                  <th>
                    <GoATableSortHeader name="requiredBy">Required by</GoATableSortHeader>
                  </th>
                  <th>
                    <GoATableSortHeader name="orderType">Order type</GoATableSortHeader>
                  </th>
                  <th>
                    <GoATableSortHeader name="orderFrom">Submitted to</GoATableSortHeader>
                  </th>
                  <th>
                    <GoATableSortHeader name="crewNumber">Crew #</GoATableSortHeader>
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {orderData?.map((order: Order, index: number) => (
                  <Fragment key={order.id}>
                    <tr>
                      <td><GoAButton
                        size="compact"
                        type="tertiary"
                        onClick={() =>
                          navigate(`/order/orderNumber/${order.orderNumber}`, { state: { redirectedFrom: 'ordersList' } })
                        }
                      >
                        {order.orderNumber}
                      </GoAButton></td>
                      <td>{order.fireNumber}</td>
                      <td>{order.forestArea}</td>
                      <td>
                        <div>{order.orderBy}</div>
                        {order.orderingForName && <div className="ordering-onbehalf-of">On behalf of {order.orderingForName?.replace(/\s*\(.*?\)/, '')}</div>}
                      </td>
                      <td>
                        <div className="badge-item"><OrderStatusBadge
                          orderStatus={order.status}
                        />
                        </div>

                      </td><td>{order.submittedDate ? dayjs.utc(order.submittedDate)
                        .tz("America/Edmonton", false).format("YYYY-MM-DD") : ""}</td>

                      {/* <td>{order.orderDate ? dayjs.utc(order.orderDate)
                        .tz("America/Edmonton", false).format("YYYY-MM-DD") : ""}</td> */}
                      <td>{moment(order.requiredBy).format("yyyy-MM-DD HH:mm")}</td>
                      <td>{order.orderType}</td>
                      <td>{order.orderFrom}</td>
                      <td>{order.crewNumber}</td>
                    </tr>
                  </Fragment>
                ))}
              </tbody>
            </GoATable>

            <div className={totalCount > 0 ? "visible pagination pagination-div" : "not-visible pagination"}>
              <GoABlock alignment="center">
                <GoABlock mb="m" alignment="center" gap="m">
                  <span style={{ whiteSpace: "nowrap" }}>Show</span>
                  <div className="dropdown-list">
                    <GoADropdown name="selPerPage" onChange={changePerPage} value={perPage} width="8ch">
                      <GoADropdownItem value="25"></GoADropdownItem>
                      <GoADropdownItem value="50"></GoADropdownItem>
                      <GoADropdownItem value="100"></GoADropdownItem>
                    </GoADropdown>
                  </div>
                  <span style={{ whiteSpace: "nowrap" }}>
                    of {totalCount} items
                  </span>
                </GoABlock>
                <GoASpacer hSpacing="fill" />
                <GoAPagination
                  itemCount={totalCount || 10}
                  perPageCount={perPage}
                  pageNumber={page}
                  onChange={changePage}
                />
              </GoABlock>
            </div>
          </div>
        </>
      ) : (
        <>
          <PageLoader visible={loading} />
          <div className="content-padding">
            <div className="left width100">
              <Breadcrumbs aria-label="breadcrumb" separator={<ArrowForwardIcon fontSize="small" />}>
                <Link
                  variant="body2"
                  onClick={() => (window.location.pathname = '/')}
                  style={{ cursor: 'pointer' }}
                >
                  Home
                </Link>
                <Typography color="textPrimary">Orders</Typography>
              </Breadcrumbs>
            </div>
            <div className="left width100">
              <div className="left width-auto">
                <PageHeader title="Orders" />
              </div>
              {hasCreateOrderPermission && <div className="right width50 paddingLeft20px input-search align-right">
                <GoAButton type="primary" trailingIcon="add" onClick={() => { navigate(`/orderNew`) }}>
                  Create order
                </GoAButton>
              </div>}
            </div>
            <div className="left width100">
              <div className="show-my-orders">
                <GoACheckbox
                  name="showMyOrder"
                  text="Only show orders I submitted"
                  onChange={
                    handleChange
                  }
                  checked={showMyOrder}
                />
              </div>
            </div>

            <div className="order-serach-wapper">
              <div className="left width100">
                <div className="left width100 order-search-container">
                  <GoAFormItem label="Search">

                    <div className="order-search-input">
                      <SearchInput maxLength={250} label="" ref={searchInputRef} search={searchOrders} clearSearch={clearSearch} placeholder="Search by Order number, Ordered by, Ship to"></SearchInput>
                    </div>
                  </GoAFormItem>
                  <div>
                    <GoAFormItem label="Fire number">
                      <MultiSelectDropdown
                        ref={fireNumberDropdownRef}
                        label=""
                        fieldName="fireNumber"
                        options={fireNumberOptions}
                        value={searchParams.fireNumber}
                        onChange={handleChange}
                        onInputChange={(event, value) => {
                          onFireNoSearchChange(value.includes("-") ? value.split("-")[0] : value);
                        }}
                      />
                    </GoAFormItem>
                  </div>
                  <div>
                    <GoAFormItem label='Submitted to'>
                      <MultiSelectDropdown
                        ref={orderFromDropdownRef}
                        label=""
                        fieldName="orderFrom"
                        options={orderFromOptions}
                        value={searchParams.orderFrom}
                        onChange={handleChange}
                      />
                    </GoAFormItem>
                  </div>
                  <div>
                    <GoAFormItem label='Forest area'>
                      <MultiSelectDropdown
                        ref={forestAreaDropdownRef}
                        label=""
                        fieldName="forestArea"
                        options={forestAreaOptions ?? []}
                        value={searchParams.forestArea}
                        onChange={handleChange}
                      />
                    </GoAFormItem>
                  </div>
                </div>
              </div>

              <div className="left width100">
                <div className="left width100 order-search-container">
                  <div>
                    <GoAFormItem label='Order status'>
                      <MultiSelectDropdown
                        ref={orderStatusDropdownRef}
                        label=""
                        fieldName="orderStatus"
                        options={orderStatusOptions}
                        value={searchParams.orderStatus}
                        onChange={handleChange}
                      />
                    </GoAFormItem>
                  </div>
                  <div>
                    <GoAFormItem label='Order type'>
                      <MultiSelectDropdown
                        ref={orderTypeDropdownRef}
                        label=""
                        fieldName="orderType"
                        options={orderTypeOptions}
                        value={searchParams.orderType}
                        onChange={handleChange}
                      />
                    </GoAFormItem>
                  </div>
                  <GoAFormItem label="Date range(start)">
                    <CustomFmF icon={searchParamsValues.startDate ? 'close' : 'calendar'} format="yyyy-MM-dd" value={searchParamsValues.startDate} onChange={(value: Date | null) => handleChange('startDate', value)} onBlur={(val: any) => { }} />
                  </GoAFormItem>
                  <GoAFormItem label="Date range(end)">
                    <CustomFmF icon={searchParamsValues.endDate ? 'close' : 'calendar'} format="yyyy-MM-dd" value={searchParamsValues.endDate} onChange={(value: Date | null) => handleChange('endDate', value)} onBlur={(val: any) => { }} />
                  </GoAFormItem>
                   <GoAFormItem label="Crew number">
                    <MultiSelectDropdown
                        ref={crewNumberDropdownRef}
                        label=""
                        fieldName="crewNumber"
                        options={crewNumberOptions}
                        value={searchParams.crewNumber}
                        onChange={handleChange}
                        onInputChange={(event, value) => {
                          onCrewNumberSearchChange(value);
                        }}
                      />
                  </GoAFormItem>
                  <div className="left width100" style={{ position: 'relative', minHeight: '100px', margin: '-25px 30px 10px 15px' }}>
                    <Link
                      variant="body2"
                      onClick={clearAll}
                      style={{
                        cursor: 'pointer',
                        font: 'var(--goa-typography-heading-s)',
                        position: 'absolute',
                        bottom: '0',
                      }}
                    >
                      Clear all
                    </Link>
                  </div>

                </div>
              </div>
            </div>

            <div className="divTable">
              <GoATable onSort={sortData} width="100%">
                <thead>
                  <tr>
                    <th>
                      <GoATableSortHeader name="orderNumber">Order #</GoATableSortHeader>
                    </th>
                    <th>
                      <GoATableSortHeader name="fireNumber">Fire #</GoATableSortHeader>
                    </th>
                    <th>
                      <GoATableSortHeader name="forestArea">Forest area</GoATableSortHeader>
                    </th>
                    <th>
                      <GoATableSortHeader name="orderBy">Ordered by</GoATableSortHeader>
                    </th>
                    <th>
                      <GoATableSortHeader name="status">Status</GoATableSortHeader>
                    </th>
                    <th>
                      <GoATableSortHeader name="submittedDate">Submitted date</GoATableSortHeader>
                    </th>
                    {/* <th>
                      <GoATableSortHeader name="orderDate">Order date</GoATableSortHeader>
                    </th> */}
                    <th>
                      <GoATableSortHeader name="requiredBy">Required by</GoATableSortHeader>
                    </th>
                    <th>
                      <GoATableSortHeader name="orderType">Order type</GoATableSortHeader>
                    </th>
                    <th>
                      <GoATableSortHeader name="orderFrom">Submitted to</GoATableSortHeader>
                    </th>
                     <th>
                      <GoATableSortHeader name="crewNumber">Crew #</GoATableSortHeader>
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {orderData?.map((order: Order, index: number) => (
                    <Fragment key={order.id}>
                      <tr>
                        <td><GoAButton
                          size="compact"
                          type="tertiary"
                          onClick={() =>
                            navigate(`/order/orderNumber/${order.orderNumber}`, { state: { redirectedFrom: 'ordersList' } })
                          }
                        >
                          {order.orderNumber}
                        </GoAButton></td>
                        <td>{order.fireNumber}</td>
                        <td>{order.forestArea}</td>
                        <td>
                          <div>{order.orderBy}</div>
                          {order.orderingForName && <div className="ordering-onbehalf-of">On behalf of {order.orderingForName?.replace(/\s*\(.*?\)/, '')}</div>}
                        </td>
                        <td>
                          <div className="badge-item"><OrderStatusBadge
                            orderStatus={order.status}
                          />
                          </div>
                        </td>
                        <td>{order.submittedDate ? dayjs.utc(order.submittedDate)
                          .tz("America/Edmonton", false).format("YYYY-MM-DD HH:mm") : ""}</td>
                        {/* <td>{order.orderDate ? dayjs.utc(order.orderDate)
                          .tz("America/Edmonton", false).format("YYYY-MM-DD") : ""}</td> */}
                        <td>{order.requiredBy ? dayjs.utc(order.requiredBy)
                          .tz("America/Edmonton", false).format("YYYY-MM-DD HH:mm") : ""}
                        </td>
                        <td>{order.orderType}</td>
                        <td>{order.orderFrom}</td>
                        <td>{order.crewNumber}</td>
                      </tr>
                    </Fragment>
                  ))}
                </tbody>
              </GoATable>

              <div className={totalCount > 0 ? "visible pagination pagination-div" : "not-visible pagination"}>
                <GoABlock alignment="center">
                  <GoABlock mb="m" alignment="center" gap="m">
                    <span style={{ whiteSpace: "nowrap" }}>Show</span>
                    <div className="dropdown-list">
                      <GoADropdown name="selPerPage" onChange={changePerPage} value={perPage} width="8ch">
                        <GoADropdownItem value="25"></GoADropdownItem>
                        <GoADropdownItem value="50"></GoADropdownItem>
                        <GoADropdownItem value="100"></GoADropdownItem>
                      </GoADropdown>
                    </div>
                    <span style={{ whiteSpace: "nowrap" }}>
                      of {totalCount} items
                    </span>
                  </GoABlock>
                  <GoASpacer hSpacing="fill" />
                  <GoAPagination
                    itemCount={totalCount || 10}
                    perPageCount={perPage}
                    pageNumber={page}
                    onChange={changePage}
                  />
                </GoABlock>
              </div>
            </div>
          </div>
          <div title="environment" hidden>{process.env.REACT_APP_ENV}</div>
        </>
      )}

    </>
  );
};

export default OrdersList;
